import React, { useEffect, useRef, useState } from 'react';
import { Menu, MenuItem } from '@mui/material';
import DragIndicatorRoundedIcon from '@mui/icons-material/DragIndicatorRounded';
import RestartAltRoundedIcon from '@mui/icons-material/RestartAltRounded';
import GasIdentity from './GasIdentity';
import './GasAssistant.css';

export const launcherStorageKey = (identity, mobile) => `crm:gas:launcher:v1:${encodeURIComponent(identity)}:${mobile ? 'mobile' : 'desktop'}`;
const limit = n => Math.max(0, Math.min(1, n));
export function launcherBounds(width, height) {
  const buttonWidth = Math.min(148, Math.max(0, width - 16)), maxX = Math.max(8, width - buttonWidth - 8);
  const maxY = Math.max(8, height - 60 - 12), minY = Math.min(maxY, (width < 600 ? 56 : 64) + 12);
  return { minX: 8, maxX, minY, maxY, buttonWidth };
}
const pixels = (position, bounds) => ({ x: bounds.minX + position.x * (bounds.maxX - bounds.minX), y: bounds.minY + position.y * (bounds.maxY - bounds.minY) });
const normal = (point, bounds) => ({ x: limit((point.x - bounds.minX) / (bounds.maxX - bounds.minX || 1)), y: limit((point.y - bounds.minY) / (bounds.maxY - bounds.minY || 1)) });
const viewport = () => ({ width: window.visualViewport?.width || window.innerWidth, height: window.visualViewport?.height || window.innerHeight });
const defaultPosition = view => normal({ x: view.width - 168, y: view.height - 80 }, launcherBounds(view.width, view.height));
function loadPosition(key, view) {
  try { const saved = JSON.parse(localStorage.getItem(key)); if (saved?.version === 1 && Number.isFinite(saved.x) && Number.isFinite(saved.y)) return { x: limit(saved.x), y: limit(saved.y) }; } catch (_) { /* Storage may be unavailable in private browsing. */ }
  return defaultPosition(view);
}

export default function GasLauncher({ identityKey, mobile, onOpen }) {
  const key = launcherStorageKey(identityKey, mobile);
  const [view, setView] = useState(viewport), [position, setPosition] = useState(() => loadPosition(key, viewport()));
  const [dragging, setDragging] = useState(false), [menu, setMenu] = useState(null);
  const gesture = useRef(null), suppressClick = useRef(false), bounds = launcherBounds(view.width, view.height);
  useEffect(() => {
    const resize = () => { gesture.current = null; setDragging(false); setView(viewport()); };
    window.addEventListener('resize', resize); window.visualViewport?.addEventListener('resize', resize);
    return () => { window.removeEventListener('resize', resize); window.visualViewport?.removeEventListener('resize', resize); };
  }, []);
  const save = next => { setPosition(next); try { localStorage.setItem(key, JSON.stringify({ version: 1, ...next })); } catch (_) { /* Movement still works without storage. */ } };
  const down = event => {
    if (event.button !== undefined && event.button !== 0) return;
    event.currentTarget.setPointerCapture?.(event.pointerId);
    gesture.current = { id: event.pointerId, x: event.clientX, y: event.clientY, initial: position, point: pixels(position, bounds), moved: false };
    suppressClick.current = false;
  };
  const move = event => {
    const g = gesture.current; if (!g || g.id !== event.pointerId) return;
    const dx = event.clientX - g.x, dy = event.clientY - g.y;
    if (!g.moved && Math.hypot(dx, dy) < 6) return;
    g.moved = true; g.next = normal({ x: g.point.x + dx, y: g.point.y + dy }, bounds); setDragging(true); setPosition(g.next);
  };
  const finish = (event, cancelled = false) => {
    const g = gesture.current; if (!g || g.id !== event.pointerId) return;
    if (cancelled) setPosition(g.initial); else if (g.moved) save(g.next);
    suppressClick.current = g.moved; gesture.current = null; setDragging(false);
    if (event.currentTarget.hasPointerCapture?.(event.pointerId)) event.currentTarget.releasePointerCapture(event.pointerId);
  };
  const reset = () => { save(defaultPosition(view)); setMenu(null); };
  const keyboard = event => {
    suppressClick.current = false;
    if (event.key === 'Home') { event.preventDefault(); reset(); return; }
    if (event.key === 'Escape' && gesture.current) { event.preventDefault(); setPosition(gesture.current.initial); gesture.current = null; setDragging(false); return; }
    const delta = { ArrowLeft: [-1,0], ArrowRight: [1,0], ArrowUp: [0,-1], ArrowDown: [0,1] }[event.key];
    if (!delta) return;
    event.preventDefault(); const point = pixels(position, bounds), step = event.shiftKey ? 24 : 8;
    save(normal({ x: point.x + delta[0] * step, y: point.y + delta[1] * step }, bounds));
  };
  const point = pixels(position, bounds);
  return <div className={`gas-orbit gas-launcher${dragging ? ' gas-dragging' : ''}`} style={{ left: point.x, top: point.y, width: bounds.buttonWidth }} data-testid="gas-launcher">
    <button className="gas-drag-handle" aria-label="Sposta Gas" aria-describedby="gas-move-hint" title="Trascina Gas · frecce per spostare · Home per ripristinare"
      onPointerDown={down} onPointerMove={move} onPointerUp={event => finish(event)} onPointerCancel={event => finish(event, true)} onKeyDown={keyboard}
      onClick={event => { if (suppressClick.current) { suppressClick.current = false; return; } setMenu(event.currentTarget); }}><DragIndicatorRoundedIcon fontSize="small" /></button>
    <button id="gas-launcher-open" className="gas-open-button" aria-label="Apri Gas" onClick={onOpen}><GasIdentity size={38} /><span>Gas</span></button>
    <span id="gas-move-hint" className="gas-sr-only">Trascina la maniglia oppure usa le frecce. Premi Home per ripristinare la posizione.</span>
    <Menu anchorEl={menu} open={!!menu} onClose={() => setMenu(null)}><MenuItem onClick={reset}><RestartAltRoundedIcon sx={{ mr: 1 }} />Ripristina posizione</MenuItem></Menu>
  </div>;
}
